import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Box, Button, Checkbox, Typography } from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";

type AdditionalInfoRow = {
  id: number;
  text: string;
  checked: boolean;
};

type Props = {
  formik: any;
  disabled?: boolean;
};

export default function AdditionalInformation({
  formik,
  disabled = false,
}: Props) {
  const pushingNotesToFormik = useRef(false);
  const [additionalInfoRows, setAdditionalInfoRows] = useState<
    AdditionalInfoRow[]
  >([{ id: Date.now(), text: "", checked: true }]);

  useLayoutEffect(() => {
    if (pushingNotesToFormik.current) {
      pushingNotesToFormik.current = false;
      return;
    }

    const existing = (formik.values.notes || []) as Array<{
      note: string;
      sendToInspector: boolean;
    }>;

    setAdditionalInfoRows((prev) => {
      const prevPayload = prev
        .filter((row) => row.text.trim() !== "")
        .map((row) => ({ note: row.text, sendToInspector: row.checked }));
      if (JSON.stringify(existing) === JSON.stringify(prevPayload)) {
        return prev;
      }

      if (existing.length === 0) {
        return [{ id: Date.now(), text: "", checked: true }];
      }

      return existing.map((row, idx) => ({
        id: Number(`${Date.now()}${idx}`),
        text: row.note || "",
        checked: !!row.sendToInspector,
      }));
    });
  }, [formik.values.notes]); //eslint-disable-line
  
  useEffect(() => {
    const notes = additionalInfoRows
      .filter((row) => row.text.trim() !== "")
      .map((row) => ({ note: row.text, sendToInspector: row.checked }));

    const currentNotes = formik.values.notes || [];
    if (JSON.stringify(notes) === JSON.stringify(currentNotes)) return;

    pushingNotesToFormik.current = true;
    formik.setFieldValue("notes", notes);
  }, [additionalInfoRows]); //eslint-disable-line

  const addNewRow = () => {
    const newRow = {
      id: Date.now(),
      text: "",
      checked: false,
    };
    setAdditionalInfoRows((prev) => [...prev, newRow]);
  };

  const deleteRow = (id: number) => {
    if (additionalInfoRows.length > 1) {
      setAdditionalInfoRows(additionalInfoRows.filter((row) => row.id !== id));
    }
  };

  const handleCheckboxChange = (id: number, checked: boolean) => {
    setAdditionalInfoRows(
      additionalInfoRows.map((row) =>
        row.id === id ? { ...row, checked } : row
      )
    );
  };

  const handleTextChange = (id: number, text: string) => {
    setAdditionalInfoRows(
      additionalInfoRows.map((row) => (row.id === id ? { ...row, text } : row))
    );
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Typography variant="h5" sx={{ fontWeight: 400, mb: 2, fontSize: "24px" }}>
        Order Notes
      </Typography>
      <Box sx={{ width: "100%", display: "flex", flexDirection: "column" }}>
        <Box
          sx={{
            display: "flex",
            pb: 1,
            mb: 1,
            alignItems: "center",
            flexWrap: "nowrap",
            gap: 2,
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "text.primary" }}>
              Note
            </Typography>
          </Box>
          <Box sx={{ width: 48, flexShrink: 0 }} />
          <Box sx={{ minWidth: 140, flexShrink: 0, textAlign: "center" }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "text.primary" }}>
              Inspector Viewable?
            </Typography>
          </Box>
        </Box>
        {additionalInfoRows.map((row) => (
          <Box
            key={row.id}
            sx={{
              display: "flex",
              alignItems: "center",
              py: 1,
              flexWrap: "nowrap",
              gap: 2,
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <textarea
                placeholder=""
                value={row.text}
                onChange={(e) => handleTextChange(row.id, e.target.value)}
                disabled={disabled}
                style={{
                  width: "100%",
                  minHeight: 40,
                  padding: 12,
                  border: "1px solid #E0E0E0",
                  borderRadius: 4,
                  resize: "vertical",
                  fontFamily: "inherit",
                  fontSize: 14,
                  boxSizing: "border-box",
                }}
              />
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
              <Button
                size="small"
                onClick={() => deleteRow(row.id)}
                disabled={disabled || additionalInfoRows.length <= 1}
                sx={{ minWidth: "auto", p: 0.5, color: "error.main" }}
              >
                <DeleteOutlineIcon fontSize="small" />
              </Button>
            </Box>
            <Box sx={{ minWidth: 140, flexShrink: 0, display: "flex", justifyContent: "center", alignItems: "center" }}>
              <Checkbox
                checked={row.checked}
                onChange={(e) => handleCheckboxChange(row.id, e.target.checked)}
                disabled={disabled}
                size="small"
                sx={{ color: "primary.main", "&.Mui-checked": { color: "primary.main" } }}
              />
            </Box>
          </Box>
        ))}
        <Button
          variant="text"
          startIcon={<AddOutlinedIcon />}
          onClick={addNewRow}
          sx={{ color: "primary.main", textTransform: "none", p: 0, alignSelf: "flex-start", mt: 1, fontSize: "13px", fontWeight: 500 }}
          disabled={disabled}
        >
          ADD ORDER NOTE
        </Button>
      </Box>
    </Box>
  );
}
